import swal from 'sweetalert';

(() => {
  angular.module('app').component('hospitalHubPatientCardComponent', {
    templateUrl: require('./hubPatientCard.html'),
    controller: HubPatientController,
    controllerAs: 'vm',
    bindings: {
      employee: '<',
      panelLocationId: '<',
      filterText: '<',
      getEmployees: '&',
    },
  });

  HubPatientController.$inject = [
    '$state',
    '$uibModal',
    'moment',
    'Hms.GuaranteeLetters.GuaranteeLetterStatus',
    'Hms.ServiceRequests.ServiceRequestType',
    'Hms.ServiceRequests.ServiceRequestStatus',
    'Hms.Policy.Status',
    'Hms.Hub.Gender',
    'abp.services.app.hospitalPanelServiceRequest',
    'abp.services.app.inpatientEmployee',
    'expiredGuaranteeLetterFlow',
  ];

  function HubPatientController(
    $state,
    $uibModal,
    moment,
    enumGuaranteeLetterStatus,
    enumServiceRequestType,
    enumServiceRequestStatus,
    enumPolicyStatus,
    enumHubGender,
    hospitalPanelServiceRequestSvc,
    inpatientEmployeeSvc,
    expiredGuaranteeLetterFlow
  ) {
    const vm = this;
    vm.loading = 0;

    vm.enums = {};
    vm.enums.guaranteeLetterStatus = enumGuaranteeLetterStatus;
    vm.enums.serviceRequestType = enumServiceRequestType;
    vm.enums.serviceRequestStatus = enumServiceRequestStatus;
    vm.enums.policyStatus = enumPolicyStatus;
    vm.enums.hubGender = enumHubGender;
    vm.employeeNoAccessMessage = null;

    if (vm.employee.patient.active === false) {
      vm.employeeNoAccessMessage = App.localize('PanelCannotApplyGLDueToOrgPolicy');
    } else if (vm.employee.isAccessible === false) {
      // Strict === false so a missing field (older backend) fails open.
      vm.employeeNoAccessMessage = App.localize('HubPatientNotAccessibleNarrative');
    }

    vm.getUserInitial = () => {
      const fullName = (vm.employee.patient.fullName || ' ').split(' ');
      let initial = '';

      for (let i = 0; i < fullName.length; i += 1) {
        if (fullName[i].length) {
          initial += fullName[i][0];
        }
      }

      if (initial && initial.length > 3) [initial] = initial;

      return initial;
    };

    vm.showCancelCheckedInRequestModal = () =>
      vm.showCancelModal(vm.enums.serviceRequestType.CheckIn.id);

    vm.showCancelModal = (requestType = vm.enums.serviceRequestType.Initial.id) => {
      $uibModal
        .open({
          templateUrl: require('../../common/cancellationModal/cancel.modal.html'),
          controller: 'clinic.views.hospital.common.cancelModal as vm',
          backdrop: 'static',
          size: 'lg',
          resolve: {
            letterRequestType: () => requestType,
            letterStatus: () => vm.employee.guaranteeLetter.glStatus,
          },
        })
        .result.then((cancellationReason) => {
          if (cancellationReason) {
            const input = {
              letterNumber: vm.employee.guaranteeLetter.glNumber,
              cancellationReason,
            };

            vm.loading += 1;

            hospitalPanelServiceRequestSvc
              .cancelGuaranteeLetterServiceRequest(input)
              .success(() => {
                abp.notify.success('Cancelled');
              })
              .finally(() => {
                vm.loading -= 1;
                vm.getEmployees();
              });
          }
        });
    };

    vm.requestGuaranteeLetter = () => {
      const payload = {
        identifierValue: vm.filterText,
        identifierType: null,
        clinicLocationId: vm.panelLocationId,
        patientId: vm.employee && vm.employee.patient ? vm.employee.patient.id : null,
      };

      vm.loading += 1;

      inpatientEmployeeSvc
        .createOrUpdateEmployeeFromHubAsync(payload)
        .then((response) => {
          const data = response && (response.data || response);
          const createdEmployeeId = data && data.employeeId;
          const caseId = data && data.caseId;
          const hubPatientId = data && data.hubPatientId;
          const hubPolicyId = data && data.hubPolicyId;

          if (createdEmployeeId) {
            $state.go('clinic.iglRequest', {
              employeeId: createdEmployeeId,
              panelLocationId: vm.panelLocationId,
              caseId,
              hubPatientId: hubPatientId ? encodeURIComponent(hubPatientId) : null,
              hubPolicyId: hubPolicyId ? encodeURIComponent(hubPolicyId) : null,
            });
          } else {
            abp.notify.error(App.localize('ErrorOccuredWhileCreatingEmployeeFromHub'));
          }
        })
        .catch(() => {
          abp.notify.error(App.localize('ErrorOccuredWhileCreatingEmployeeFromHub'));
        })
        .finally(() => {
          vm.loading -= 1;
        });
    };

    vm.navigateToTimeline = () => {
      if (!vm.employee || !vm.employee.guaranteeLetter) return;
      $state.go('clinic.patientAdmissionDetails', {
        letterNumber: vm.employee.guaranteeLetter.glNumber,
      });
    };

    const checkIn = (data) => {
      vm.loading += 1;
      hospitalPanelServiceRequestSvc
        .checkIn({
          guaranteeLetterNumber: vm.employee.guaranteeLetter.glNumber,
          remarks: data.reason,
          actualAdmissionDate: data.actualAdmissionDate,
          newAttachments: data.newAttachments,
        })
        .success((resp) => {
          if (resp.printUrl !== '') {
            abp.notify.info(App.localize('PatientXCheckedIn', vm.employee.name));
            window.open(resp.printUrl);
            vm.navigateToTimeline();
          } else {
            if (resp.isEarlyCheckIn) abp.notify.info(App.localize('EarlyCheckInRequestSubmitted'));
            else abp.notify.info(App.localize('LateCheckInRequestSubmitted'));
            vm.refreshFunction();
          }
        })
        .finally(() => {
          vm.loading -= 1;
        });
    };

    // An expired guarantee letter cannot be checked in or rescheduled — the hospital is offered
    // cancel-and-reapply instead (HM-11144).

    vm.showExpiredGuaranteeLetter = () => {
      const guaranteeLetter = vm.employee.guaranteeLetter;
      const patient = vm.employee.patient || {};

      expiredGuaranteeLetterFlow.prompt({
        letterNumber: guaranteeLetter.glNumber,
        scheduledAdmissionTime: guaranteeLetter.scheduledAdmissionTime,
        reapplyParams: {
          employeeId: vm.employee.id,
          panelLocationId: vm.panelLocationId,
          hubPatientId: patient.hubPatientId ? encodeURIComponent(patient.hubPatientId) : null,
          hubPolicyId: patient.hubPolicyId ? encodeURIComponent(patient.hubPolicyId) : null,
        },
        onCancelled: () => vm.getEmployees(),
      });
    };

    vm.checkInEmployee = () => {
      if (expiredGuaranteeLetterFlow.isExpired({
        status: vm.employee.guaranteeLetter.glStatus,
        checkInExpiryTime: vm.employee.guaranteeLetter.checkInExpiryTime,
      })) {
        vm.showExpiredGuaranteeLetter();
        return;
      }

      const today = moment().format('YYYY-MM-DD');
      const scheduledTime = moment(vm.employee.guaranteeLetter.scheduledAdmissionTime).format(
        'YYYY-MM-DD'
      );
      if (!scheduledTime) return;

      if (scheduledTime === today) {
        swal(
          {
            title: App.localize('ConfirmAdmission'),
            text: App.localize('ConfirmAdmissionNarrative'),
            confirmButtonColor: '#1ab394',
            confirmButtonText: App.localize('Yes'),
            cancelButtonText: App.localize('No'),
            showCancelButton: true,
            closeOnConfirm: false,
          },
          (isConfirm) => {
            if (isConfirm) {
              checkIn('');
              vm.employee.isGLSubmittedOrApproved = false;
              vm.employee.checkedInTime = vm.employee.guaranteeLetter.scheduledAdmissionTime;
            }
            swal.close();
          }
        );
      } else {
        $uibModal
          .open({
            templateUrl: require('../admission/admission.modal.html'),
            controller: 'clinic.views.hospital.patient.admission as vm',
            backdrop: 'static',
            resolve: {
              scheduleDate: () => vm.employee.guaranteeLetter.scheduledAdmissionTime,
              expiryDate: () => vm.employee.guaranteeLetter.expiryTime,
              letterNumber: () => vm.employee.guaranteeLetter.glNumber,
            },
          })
          .result.then((data) => {
            if (data.isCheckIn) checkIn(data);
          });
      }
    };
  }
})();
